/**
 * LEAD-360-CONFORMANCE-B1 -- OverflowMenu (F1 NEW PATTERNS item 7 candidate
 * "OverflowMenu"; docs/design-studio/lead-360/IMPLEMENTATION-CONFORMANCE-CONTRACT.md
 * "Overflow action menu | new OverflowMenu composition over EXISTING
 * writers | New composition of existing truths").
 *
 * The masthead's whole-record action home: a "..." trigger that opens a
 * popover of verb rows, a separator, then tagged future doors (F1 NEW
 * PATTERNS item 6 "IntentTag" -- the amber DESIGN-ONLY -
 * REQUIRES FUNCTIONAL APPROVAL chip). PRESENTATION-ONLY: every item's
 * onClick is caller-supplied (an EXISTING lawful writer); an item with no
 * onClick renders disabled (no dead-door silent failure) unless it carries
 * an intent tag, which renders inert by design (LES-016 exception: a
 * DESIGN-ONLY door explicitly never opens anything real).
 *
 * Click-outside + Escape dismissal mirrors the proven ui/list/ColumnChooser.tsx
 * popover idiom -- one implementation, not a second one.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { QuietAction } from './PrimaryAction';
import { DESIGN_ONLY_TAG_LABEL } from './lexicon';
import './signatures.css';

export interface OverflowMenuItem {
  readonly key: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly onSelect?: () => void;
  readonly disabled?: boolean;
  /** Renders the amber DESIGN-ONLY tag and forces the item inert. */
  readonly designOnly?: boolean;
  /** Visually separates this item from the previous group (a divider above it). */
  readonly separatorBefore?: boolean;
  readonly testId?: string;
}

export interface OverflowMenuProps {
  readonly items: readonly OverflowMenuItem[];
  /** Accessible label for the trigger (default "More actions"). */
  readonly triggerLabel?: string;
  readonly testId?: string;
}

export default function OverflowMenu({
  items,
  triggerLabel = 'More actions',
  testId = 'ac-overflow',
}: OverflowMenuProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className="ac-overflow" ref={ref} data-testid={testId}>
      <QuietAction
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={triggerLabel}
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">&#8943;</span>
      </QuietAction>
      {/* Always mounted (never conditionally unmounted): toggled via
          `hidden` rather than `{open ? ... : null}` so the closed-by-
          default menu content is still present in a STATIC render
          (react-dom/server -- the repo's pinned test idiom) and pinnable
          by unit tests, exactly like the open state would be. */}
      <div
        className="ac-overflow__menu"
        role="menu"
        hidden={!open}
        data-testid={`${testId}-menu`}
      >
        {items.map((item) => (
          <div key={item.key}>
            {item.separatorBefore ? (
              <div className="ac-overflow__sep" role="separator" />
            ) : null}
            <QuietAction
              role="menuitem"
              className="ac-overflow__item"
              data-testid={item.testId ?? `${testId}-item-${item.key}`}
              disabled={item.disabled === true || item.designOnly === true}
              onClick={
                item.designOnly === true
                  ? undefined
                  : (): void => {
                      setOpen(false);
                      item.onSelect?.();
                    }
              }
            >
              {item.icon ? (
                <span className="ac-overflow__icon" aria-hidden="true">
                  {item.icon}
                </span>
              ) : null}
              <span className="ac-overflow__label">{item.label}</span>
              {item.designOnly === true ? (
                <span
                  className="ac-tag ac-overflow__tag"
                  data-testid={`${testId}-item-${item.key}-designonly`}
                >
                  {DESIGN_ONLY_TAG_LABEL}
                </span>
              ) : null}
            </QuietAction>
          </div>
        ))}
      </div>
    </div>
  );
}
